"use client";

import Image from "next/image";
import Link from "next/link";
import { ArrowRight, ExternalLink } from "lucide-react";
import { useGeoLocation } from "@/lib/hooks/useGeoLocation";

export interface PlayerCardProps {
  id: string;
  iconUrl: string;
  title: string;
  version?: string;
  description: string;
  supportingDevices: string[];
  downloadDescription: string;
  downloadUrl?: string;
  downloadLabel?: string;
  installationGuideUrl: string;
  learnMoreUrl: string;
  iconAlt: string;
  color: string;
  regionRestriction?: "US";
  iconWidth?: number;
  iconHeight?: number;
  iconClassName?: string;
  compact?: boolean;
}

export function PlayerCard({
  id,
  iconUrl,
  title,
  version,
  description,
  supportingDevices,
  downloadUrl,
  downloadLabel = "Download",
  installationGuideUrl,
  learnMoreUrl,
  iconAlt,
  regionRestriction,
  iconWidth = 28,
  iconHeight = 28,
  iconClassName = "object-contain h-5 w-5",
  compact = false,
}: PlayerCardProps) {
  const { geoData, isLoading } = useGeoLocation();

  if (regionRestriction === "US") {
    if (isLoading) return null;
    if (!geoData || geoData.countryCode !== "US") return null;
  }

  if (!compact) {
    return (
      <div
        id={id}
        className="mb-24 flex w-full flex-col justify-between gap-5 rounded-[10px] border border-[#e2e6ec] bg-white shadow-default md:flex-row"
      >
        <div className="flex w-full flex-col justify-center gap-2.5 bg-slate-50 p-7 text-left md:max-w-[400px] lg:items-start">
          <Image
            src={iconUrl}
            alt={iconAlt}
            title={iconAlt}
            width={iconWidth}
            height={iconHeight}
            className={iconClassName}
          />
          <div className="py-10">
            <h2 className="mb-2.5 text-3xl font-semibold text-text-primary">{title}</h2>
            <p className="max-w-md text-base leading-relaxed text-text-secondary">{description}</p>
          </div>
        </div>
        <div className="flex flex-col justify-center gap-8 px-7 py-12 md:gap-10 md:py-20 lg:w-[calc(100%-400px)]">
          <div>
            <h3 className="mb-3 text-lg font-semibold text-text-primary">Download</h3>
            <p className="text-md leading-relaxed text-text-secondary">{description}</p>
          </div>
          <ul className="flex flex-wrap gap-4">
            {supportingDevices.map((device, index) => (
              <li
                key={index}
                className="rounded-full border border-secondary/40 bg-secondary/5 px-3 py-1.5 text-sm text-secondary"
              >
                {device}
              </li>
            ))}
          </ul>
        </div>
      </div>
    );
  }

  const showDownload = downloadUrl && downloadUrl !== "#";

  return (
    <article
      id={id}
      className="flex h-full flex-col rounded-xl border border-gray-200 bg-white p-5 shadow-sm"
    >
      <div className="mb-4 flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-gray-100">
          <Image
            src={iconUrl}
            alt={iconAlt}
            title={iconAlt}
            width={iconWidth}
            height={iconHeight}
            className={iconClassName}
          />
        </div>
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-text-primary leading-tight">{title}</h3>
          {version ? (
            <p className="mt-0.5 text-xs text-text-secondary">{version}</p>
          ) : null}
        </div>
      </div>

      <p className="mb-4 flex-1 text-sm leading-relaxed text-text-secondary">{description}</p>

      <div className="mb-4 rounded-lg bg-gray-50 px-4 py-3">
        <p className="mb-2 text-[10px] font-bold uppercase tracking-wide text-text-secondary">
          Compatible Devices
        </p>
        <ul className="flex flex-wrap gap-1.5">
          {supportingDevices.map((device, index) => (
            <li
              key={index}
              className="rounded-full border border-gray-200 bg-white px-2.5 py-0.5 text-xs text-text-primary"
            >
              {device}
            </li>
          ))}
        </ul>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-gray-100 pt-4 text-sm">
        <Link
          href={installationGuideUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 font-medium text-primary hover:underline"
        >
          Installation guide
          <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
        </Link>
        {showDownload ? (
          <Link
            href={downloadUrl}
            target={downloadUrl.startsWith("http") ? "_blank" : "_self"}
            rel={downloadUrl.startsWith("http") ? "noopener noreferrer" : undefined}
            className="font-medium text-primary hover:underline"
          >
            {downloadLabel}
          </Link>
        ) : null}
        <Link
          href={learnMoreUrl}
          className="inline-flex items-center gap-1 font-medium text-accent hover:underline"
        >
          Know more
          <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
        </Link>
      </div>
    </article>
  );
}
